import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, CircleDashed, ClipboardCheck, Clock, History, Loader2, Lock, Star, TriangleAlert, X } from "lucide-react";
import { ApiError, api, errorMessage, post } from "@/lib/api";
import {
  partsFromBody,
  resolveSubmissionSettings,
  type BodyPart,
  type ContinueSettings,
  type SubmissionSettings,
} from "@/lib/activityAnatomy";
import {
  lockableVideoParts,
  mergeWatchEntry,
  normalizeWatchMap,
  watchMapSatisfied,
  type WatchMap,
} from "@/lib/mediaWatch";
import StudentBodyParts from "@/components/student/StudentBodyParts";
import StudentKnowledgeBanner from "@/components/student/StudentKnowledgeBanner";
import StudentListen from "@/components/student/StudentListen";
import StudentSlideDecks from "@/components/student/StudentSlideDecks";
import StudentKeyTerms from "@/components/student/StudentKeyTerms";
import { inferSlideMode, normalizeSlideDecks } from "@/lib/slideDecks";
import StudentQuestions, {
  type QuestionResponse,
  type QuestionResult,
  type StudentQuestion,
} from "@/components/student/StudentQuestions";
import StudentDiscussion, { type DiscussionState } from "@/components/student/StudentDiscussion";
import WorkThread, { WorkThreadTrigger, messageSubmissionId, type WorkThreadData } from "@/components/courses/WorkThread";
import { ITEM_ICON, ITEM_LABEL } from "./itemTypes";
import { Mascot } from "./Mascot";
import { finishedExamLine } from "./celebrations";
import { responsesAreComplete } from "@/lib/questionScoring";
import { activitySpeakText } from "@/lib/studentSpeak";

/**
 * One activity, opened.
 *
 * Opening records the visit. Questions save as a draft attempt, then Submit
 * scores them. Complete is a separate last click when continue settings say so.
 *
 * HTML parts on `body[]` are written by teachers and sanitized ON THE
 * SERVER before they are sent (services/sanitizeContent.js). That is still the
 * only dangerouslySetInnerHTML in the student app — if that sanitizer is ever
 * bypassed or removed, this is the line that becomes a hole.
 */

type Activity = {
  id: string;
  courseId: string;
  type: string;
  title: string | null;
  body: BodyPart[] | null;
  content: Record<string, unknown> | null;
  dueAt: string | null;
  completionMode: "on_complete" | "on_submit" | "on_discussion" | "on_open" | "by_teacher" | "unknown";
  continueSettings?: ContinueSettings;
  continueAction?: "complete" | "submit" | "discussion";
  submissionSettings?: SubmissionSettings;
  studentMayMessage?: boolean;
  hasQuestions?: boolean;
  maxAttempts?: number | null;
  contentVersion?: number;
  pointsPossible: number | null;
};

type Progress = {
  id?: string;
  status: "in_progress" | "completed";
  completedAt: string | null;
  openedAt?: string | null;
  submittedAt?: string | null;
  pointsEarned: number | null;
  pointsPossible?: number | null;
  contentVersion?: number | null;
  teacherComment?: string | null;
  mediaWatch?: WatchMap;
};

type Submission = {
  id: string;
  status: "draft" | "submitted";
  gradingStatus: "not_required" | "pending" | "complete";
  isPrimary: boolean;
  attempt: number;
  pointsEarned: number | null;
  pointsPossible: number | null;
  /** Points on this attempt nobody has marked yet — deliberately not part of
   *  pointsPossible, so a handed-in paper never reads as points already lost. */
  pointsPending: number | null;
  submittedAt: string | null;
  contentVersion?: number | null;
  teacherComment?: string | null;
  gradedAt?: string | null;
  files?: Array<{ id?: string; name?: string; url?: string; s3Key?: string; mime?: string }>;
  responses: Array<{
    questionId: string;
    response: QuestionResponse;
    isCorrect?: boolean | null;
    correct?: { optionId?: number; optionIds?: number[]; answer?: string; pairs?: Record<string, number> };
    pointsEarned?: number | null;
    pointsPossible?: number | null;
    pending?: boolean;
    matchMarks?: Record<string, boolean>;
    teacherComment?: string | null;
    gradedAt?: string | null;
  }>;
};

/**
 * The clock on a timed activity. Null when there is no time limit. Before
 * Start only `limitMinutes` is meaningful; after it the deadline is the
 * server's, and the page merely counts down towards it.
 */
type Timing = {
  limitMinutes: number;
  started: boolean;
  startedAt: string | null;
  deadline: string | null;
  remainingSeconds: number | null;
  expired: boolean;
};

type Neighbor = {
  id: string;
  title: string | null;
  type: string;
  locked: boolean;
};

type AttemptSummary = {
  id: string;
  status: "draft" | "submitted";
  gradingStatus: "not_required" | "pending" | "complete";
  isPrimary: boolean;
  attempt: number;
  submittedAt: string | null;
  pointsEarned: number | null;
  pointsPossible: number | null;
  pointsPending: number | null;
};

type OpenPayload = {
  activity: Activity;
  progress: Progress;
  questions?: StudentQuestion[];
  submission?: Submission | null;
  primary?: Submission | null;
  attempts?: AttemptSummary[];
  attemptCount?: number;
  maxAttempts?: number | null;
  timing?: Timing | null;
  canRetry?: boolean;
  dueLocked?: boolean;
  passed?: boolean | null;
  unlocksComplete?: boolean;
  submissionSettings?: SubmissionSettings;
  lessonChanged?: boolean;
  viewingHistory?: boolean;
  /** The current activity still has questions. An open attempt may be showing an older paper. */
  liveHasQuestions?: boolean;
  neighbors?: { previous: Neighbor | null; next: Neighbor | null };
  discussion?: DiscussionState | null;
};

function clock(seconds: number) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}

/** What Start says. "Start the exam" reads better than a bare "Start". */
function startLabel(type: string) {
  switch (type) {
    case "exam":
      return "Start Exam";
    case "assessment":
      return "Start the assessment";
    case "homework":
      return "Start the homework";
    default:
      return "Start";
  }
}

function dwellRemaining(openedAt: string | null | undefined, minutes: number, now: number) {
  if (!minutes || !openedAt) return 0;
  const elapsed = (now - new Date(openedAt).getTime()) / 60000;
  return Math.max(0, minutes - elapsed);
}

function keyTermsFromContent(content: Record<string, unknown> | null | undefined) {
  const raw = content?.terms;
  if (!Array.isArray(raw)) return [];
  const out: { term: string; definition: string }[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const rec = row as { term?: unknown; definition?: unknown };
    const term = String(rec.term || "").trim();
    const definition = String(rec.definition || "").trim();
    if (!term && !definition) continue;
    out.push({ term, definition });
  }
  return out;
}

type ActionsPin = {
  frozen: boolean;
  height: number;
  left: number;
  width: number;
  bottom: number;
  translateY: number;
};

function pinEq(a: ActionsPin | null, b: ActionsPin) {
  return (
    !!a &&
    a.frozen === b.frozen &&
    a.height === b.height &&
    a.left === b.left &&
    a.width === b.width &&
    a.bottom === b.bottom &&
    a.translateY === b.translateY
  );
}

function snapPx(n: number) {
  return Math.round(n * 2) / 2;
}

type ChatPin = { top: number; right: number; chipH: number; maxPanel: number };

function chatPinEq(a: ChatPin | null, b: ChatPin) {
  return (
    !!a &&
    a.top === b.top &&
    a.right === b.right &&
    a.chipH === b.chipH &&
    a.maxPanel === b.maxPanel
  );
}

/**
 * Sit the Chat chip on the activity column, at the top of `.student-main`.
 * `position: fixed; right: 0.8rem` parked it in the window chrome, away from
 * the 760px reading viewport.
 */
function usePinChatToMain(dockRef: RefObject<HTMLDivElement | null>, enabled: boolean) {
  const [pin, setPin] = useState<ChatPin | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setPin(null);
      return;
    }
    const dock = dockRef.current;
    const scroller = dock?.closest(".student-main");
    const page = dock?.closest(".student-page");
    if (!dock || !(scroller instanceof HTMLElement) || !(page instanceof HTMLElement)) {
      setPin(null);
      return;
    }

    let frame = 0;
    const measure = () => {
      frame = 0;
      const root = scroller.getBoundingClientRect();
      const col = page.getBoundingClientRect();
      const inset = 12;
      const chipH = dock.offsetHeight || 36;
      const top = snapPx(root.top + inset);
      const right = snapPx(Math.max(inset, window.innerWidth - col.right));
      const next: ChatPin = {
        top,
        right,
        chipH,
        maxPanel: snapPx(Math.max(160, root.bottom - top - chipH - 16)),
      };
      setPin((prev) => (chatPinEq(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(scroller);
    ro.observe(page);
    ro.observe(dock);
    scroller.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    measure();
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      ro.disconnect();
      scroller.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [enabled, dockRef]);

  return pin;
}

/**
 * Keep the action row on screen without a snap. It is always `position: fixed`
 * to the floor of `.student-main`, then `translateY` pulls it up to sit on its
 * in-flow slot until that slot would go below the fold — then it stays on the
 * floor. Short activities still read as in-flow (the bar rides the slot);
 * long ones never hide Check / Previous / Next.
 *
 * `position: sticky; bottom: 0` cannot do this — sticky `bottom` kicks in when
 * scrolling *up*, and a last child has no room to travel inside its parent.
 * The scroll container is the main column, not the window (rail + mobile bar).
 */
function usePinActionsWhenOffscreen(
  slotRef: RefObject<HTMLDivElement | null>,
  barRef: RefObject<HTMLDivElement | null>,
  enabled: boolean,
) {
  const [pin, setPin] = useState<ActionsPin | null>(null);
  const frozenRef = useRef(false);

  useLayoutEffect(() => {
    if (!enabled) {
      frozenRef.current = false;
      setPin(null);
      return;
    }
    const slot = slotRef.current;
    const bar = barRef.current;
    if (!slot || !bar) return;
    const scroller = slot.closest(".student-main");
    if (!(scroller instanceof HTMLElement)) return;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const root = scroller.getBoundingClientRect();
      const home = slot.getBoundingClientRect();
      const height = bar.offsetHeight;
      const bottom = snapPx(Math.max(0, window.innerHeight - root.bottom));
      const pinTop = root.bottom - height;
      const translateY = snapPx(Math.min(0, home.top - pinTop));
      // Chrome hysteresis so the frost does not flicker on the last pixels of
      // scroll — the bar itself still follows `translateY` every frame.
      let frozen = frozenRef.current;
      frozen = frozen ? translateY > -28 : translateY > -6;
      frozenRef.current = frozen;
      const next: ActionsPin = {
        frozen,
        height,
        left: snapPx(root.left),
        width: snapPx(root.width),
        bottom,
        translateY,
      };
      setPin((prev) => (pinEq(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    const io = new IntersectionObserver(schedule, { root: scroller, threshold: [0, 0.01, 1] });
    io.observe(slot);
    const ro = new ResizeObserver(schedule);
    ro.observe(scroller);
    ro.observe(slot);
    ro.observe(bar);
    scroller.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    measure();
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      io.disconnect();
      ro.disconnect();
      scroller.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [enabled, slotRef, barRef]);

  return pin;
}

function studentQuestionDomId(questionId: string) {
  return `student-q-${questionId}`;
}

function jumpToStudentQuestion(questionId: string) {
  const node = document.getElementById(studentQuestionDomId(questionId));
  node?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function answersFromSubmission(submission: Submission | null | undefined): Record<string, QuestionResponse> {
  const out: Record<string, QuestionResponse> = {};
  for (const row of submission?.responses || []) {
    out[row.questionId] = row.response || {};
  }
  return out;
}

function formatSubmittedAt(iso: string | null | undefined) {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function attemptsNewestFirst(rows: AttemptSummary[]) {
  return [...rows].sort((a, b) => {
    if (a.status === "draft" && b.status !== "draft") return -1;
    if (b.status === "draft" && a.status !== "draft") return 1;
    return (b.attempt || 0) - (a.attempt || 0);
  });
}

function formatAttemptScore(row: AttemptSummary) {
  if (row.status === "draft") return "In progress";
  if (row.pointsPossible == null) return "Submitted";
  const earned = row.pointsEarned == null ? "—" : String(row.pointsEarned);
  const pending = row.pointsPending ? ` · ${row.pointsPending} unmarked` : "";
  return `${earned} / ${row.pointsPossible}${pending}`;
}

/**
 * Banner on a locked paper: which attempt is on screen, that it cannot be
 * edited, and (when retries remain) that Try Again is the way to change it.
 *
 * Open loads the primary attempt, which is usually also the latest. When a
 * better earlier try still counts, say so — "last submission" would be a lie.
 */
function submittedReviewCopy(opts: {
  attempt: number | undefined;
  attemptCount: number;
  isPrimary: boolean;
  submittedAt: string | null;
  canRetry: boolean;
  hasOpenDraft: boolean;
}) {
  const when = formatSubmittedAt(opts.submittedAt);
  const isLatest = (opts.attempt || 0) >= (opts.attemptCount || 0) || opts.attemptCount <= 1;
  const title = !isLatest
    ? opts.isPrimary
      ? "This is the submission that counts"
      : `This is attempt ${opts.attempt}`
    : "This is your last submission";
  const lead = !isLatest && opts.isPrimary
    ? when
      ? `This is not your latest try — it is the one that counts toward your grade. Handed in ${when}.`
      : "This is not your latest try — it is the one that counts toward your grade."
    : when
      ? `Handed in ${when}.`
      : "Your answers are already in.";
  const locked = "The form is locked, so these answers cannot be changed here.";
  const retry = opts.canRetry ? " Click Try Again below to start over and resubmit." : "";
  const draft = opts.hasOpenDraft
    ? " You have a new attempt in progress — tap the attempt chip to go back to it."
    : "";
  return { title, body: `${lead} ${locked}${retry}${draft}` };
}

function retryHint(attemptsLeft: number | null, canContinue: boolean) {
  const next = canContinue ? " Or click Next to continue." : "";
  if (attemptsLeft == null) return `Start over and resubmit.${next}`;
  if (attemptsLeft === 1) return `Start over and resubmit — you have 1 attempt left.${next}`;
  return `Start over and resubmit — you have ${attemptsLeft} attempts left.${next}`;
}

/**
 * Max on the slice already marked. Teacher-pending points sit outside
 * pointsPossible, so they must not look like points the student dropped.
 * All-pending (no marked possible) is treated as max — they have not missed
 * anything that has a score yet.
 */
function markedSliceIsMax(submission: Submission | null | undefined) {
  if (!submission || submission.status !== "submitted") return false;
  const possible = submission.pointsPossible;
  const earned = submission.pointsEarned;
  if (possible == null || possible <= 0) return true;
  return earned != null && earned >= possible;
}

function shouldOfferRetry(row: OpenPayload) {
  return Boolean(row.canRetry && row.submission?.status === "submitted" && !markedSliceIsMax(row.submission));
}

function previousAttemptLine(
  primary: Submission | null | undefined,
  showPoints: boolean,
  grade?: { earned: number | null; possible: number | null } | null,
) {
  const which = !primary || primary.status !== "submitted"
    ? "Your previous attempt"
    : primary.attempt
      ? `Attempt ${primary.attempt}`
      : "Your previous attempt";
  const saved = !primary || primary.status !== "submitted"
    ? "Your previous attempt is saved."
    : !showPoints || primary.pointsPossible == null
      ? `${which} is saved.`
      : `${which} scored ${primary.pointsEarned == null ? "—" : String(primary.pointsEarned)} out of ${primary.pointsPossible}.`;
  if (!showPoints || !grade || grade.earned == null || grade.possible == null) return saved;
  const same =
    primary?.status === "submitted" &&
    primary.pointsEarned === grade.earned &&
    primary.pointsPossible === grade.possible;
  if (same) return saved;
  return `Your score on this activity is ${grade.earned} out of ${grade.possible}. ${saved}`;
}

function retryPromptCopy(opts: { showPoints: boolean; attemptsLeft: number | null }) {
  const left =
    opts.attemptsLeft == null
      ? ""
      : opts.attemptsLeft === 1
        ? " You have 1 attempt left."
        : ` You have ${opts.attemptsLeft} attempts left.`;
  if (opts.showPoints) {
    return `You didn't get as many points as you could on this attempt.${left} Would you like to try again, or continue to the next activity?`;
  }
  // Scores are hidden — still offer the choice, but do not say they missed
  // points or which answers were off.
  return `You have another attempt.${left} Would you like to try again, or continue to the next activity?`;
}

function scoreNoticeCopy(opts: {
  earned: number | null;
  possible: number | null;
  pending: number | null;
  gradingPending: boolean;
  showIncorrect: boolean;
  showQuestionPoints: boolean;
  passed: boolean | null;
  attemptEarned?: number | null;
  attemptPossible?: number | null;
}) {
  const earned = opts.earned ?? 0;
  const possible = opts.possible ?? 0;
  const title =
    possible > 0
      ? `You got ${earned} out of ${possible} ${possible === 1 ? "point" : "points"}`
      : "Your answers are in";
  if (opts.gradingPending) {
    const body = opts.pending
      ? `There ${opts.pending === 1 ? "is still a question" : "are still some questions"} your teacher needs to grade before you get your real score.`
      : "Your teacher still has some of this to mark before you get your real score.";
    return { title, body };
  }
  const attemptLine =
    opts.attemptPossible != null
      ? ` This attempt scored ${opts.attemptEarned == null ? "—" : opts.attemptEarned} out of ${opts.attemptPossible}.`
      : "";
  if (opts.showIncorrect) {
    return {
      title,
      body:
        (opts.passed === false ? "Have a look at the ones marked in red." : "Your answers are marked below.") +
        attemptLine,
    };
  }
  if (opts.showQuestionPoints) {
    return {
      title,
      body: `Points on each question are below. A low score means that one was off.${attemptLine}`,
    };
  }
  return { title, body: `Your answers are in.${attemptLine}` };
}

function resultsFromSubmission(submission: Submission | null | undefined): Record<string, QuestionResult> {
  if (!submission || submission.status !== "submitted") return {};
  const out: Record<string, QuestionResult> = {};
  for (const row of submission.responses || []) {
    out[row.questionId] = {
      questionId: row.questionId,
      isCorrect: row.isCorrect ?? null,
      pointsEarned: row.pointsEarned ?? null,
      pointsPossible: row.pointsPossible ?? null,
      pending: row.pending === true,
      teacherComment: row.teacherComment ?? null,
      gradedAt: row.gradedAt ?? null,
      correct: row.correct,
      matchMarks: row.matchMarks,
    };
  }
  return out;
}

function ActivityView() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<OpenPayload | null>(null);
  const [answers, setAnswers] = useState<Record<string, QuestionResponse>>({});
  const [error, setError] = useState("");
  const [pathLocked, setPathLocked] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState<"saving" | "saved" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryPrompt, setRetryPrompt] = useState<{ showPoints: boolean; attemptsLeft: number | null } | null>(
    null,
  );
  const [versionConfirm, setVersionConfirm] = useState<null | "retry" | "finish">(null);
  const [tryAgainCue, setTryAgainCue] = useState(0);
  const [examCheer, setExamCheer] = useState<string | null>(null);
  const [attemptsOpen, setAttemptsOpen] = useState(false);
  const [workThread, setWorkThread] = useState<WorkThreadData | null>(null);
  const [threadTab, setThreadTab] = useState("all");
  const [threadSending, setThreadSending] = useState(false);
  const [searchParams] = useSearchParams();
  const [threadOpen, setThreadOpen] = useState(() => searchParams.get("chat") === "1");
  const [switchingAttempt, setSwitchingAttempt] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [localWatch, setLocalWatch] = useState<WatchMap>({});
  const attemptsMenuRef = useRef<HTMLDivElement>(null);
  const tryAgainRef = useRef<HTMLDivElement>(null);
  const watchMap = useMemo(() => {
    const merged = { ...normalizeWatchMap(payload?.progress?.mediaWatch) };
    for (const [key, entry] of Object.entries(localWatch)) {
      merged[key] = mergeWatchEntry(merged[key], entry);
    }
    return merged;
  }, [payload?.progress?.mediaWatch, localWatch]);
  const watchRef = useRef(watchMap);
  watchRef.current = watchMap;

  // What the answers looked like when this attempt was loaded. Comparing against
  // it is what distinguishes "the student changed something" from "the payload
  // just arrived" — a one-shot skip flag gets this wrong and swallows the first
  // answer a student gives.
  const loadedAnswers = useRef<string>("{}");
  // Latest answers, even while a save is in flight. The matching board (and
  // every other field) can change faster than the server round-trip; reading
  // from state in the save closure is how a prior wire comes back.
  const answersRef = useRef<Record<string, QuestionResponse>>({});
  const payloadRef = useRef<OpenPayload | null>(null);
  const saveGate = useRef({ inFlight: false, queued: false, epoch: 0 });
  answersRef.current = answers;
  payloadRef.current = payload;
  // The draft whose clock this page already handed in at zero, so the
  // auto-submit fires once per attempt and never for the next one.
  const autoSubmittedFor = useRef<string | null>(null);
  // Submitted question papers complete themselves so Finish is not a second
  // click on a locked form. Keyed by activity so a retry cannot fire twice.
  const autoCompletedFor = useRef<string | null>(null);
  const [timeRanOut, setTimeRanOut] = useState(false);
  const actionsSlotRef = useRef<HTMLDivElement>(null);
  const actionsBarRef = useRef<HTMLDivElement>(null);
  const chatDockRef = useRef<HTMLDivElement>(null);
  const pinActions = usePinActionsWhenOffscreen(
    actionsSlotRef,
    actionsBarRef,
    Boolean(payload && !error && !payload.discussion),
  );
  const chatEnabled = Boolean(
    payload &&
      (payload.activity.studentMayMessage ||
        workThread?.canReply ||
        (workThread?.entries && workThread.entries.length > 0)),
  );
  const pinChat = usePinChatToMain(chatDockRef, chatEnabled);

  const applyPayload = (next: OpenPayload, mode: "replace" | "ack" = "replace", sent?: string) => {
    setPayload((prev) => {
      const merged: OpenPayload = {
        ...next,
        neighbors: next.neighbors || prev?.neighbors,
      };
      return merged;
    });
    // Autosave must not paint the server copy over a newer local answer.
    // If what we sent is still what is on screen, just advance the baseline
    // so we do not save the same payload again.
    if (mode === "ack") {
      const live = JSON.stringify(answersRef.current);
      if (sent && sent === live) loadedAnswers.current = sent;
      return;
    }
    const incoming = answersFromSubmission(next.submission);
    // Baseline for the autosave comparison above — set BEFORE the state update
    // so the effect that follows sees it.
    loadedAnswers.current = JSON.stringify(incoming);
    answersRef.current = incoming;
    setAnswers(incoming);
  };

  const postWork = async (
    path: "start" | "answers" | "submit" | "complete",
    body: Record<string, unknown> = {},
    mode: "replace" | "ack" = "replace",
    epoch?: number,
  ) => {
    if (!activityId) return null;
    const env = await post<{ success: boolean; data: OpenPayload }>(
      `/api/student/activities/${encodeURIComponent(activityId)}/${path}`,
      body,
    );
    if (epoch != null && epoch !== saveGate.current.epoch) return env.data;
    applyPayload(env.data, mode, path === "answers" ? JSON.stringify(body.answers ?? {}) : undefined);
    return env.data;
  };

  const advanceToNext = async () => {
    setCompleting(true);
    setError("");
    try {
      const nextPayload = await postWork("complete", { watch: watchRef.current });
      const courseId = nextPayload?.activity?.courseId;
      // Discussions are a conversation, not a hop to the next worksheet.
      // Send them back to the path so they pick the next step themselves.
      if (nextPayload?.activity?.type === "discussion" && courseId) {
        navigate(`/student/path/${encodeURIComponent(courseId)}`, {
          state: { returnToActivityId: nextPayload.activity.id },
        });
        return;
      }
      const onward = nextPayload?.neighbors?.next;
      if (onward && !onward.locked) {
        navigate(`/student/activity/${onward.id}`);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setCompleting(false);
    }
  };

  const afterHandIn = (row: OpenPayload | null | undefined) => {
    if (!row?.submission || row.submission.status !== "submitted") return;
    const offerRetry = shouldOfferRetry(row);
    if (offerRetry) {
      const vis = resolveSubmissionSettings({
        submissionSettings: row.submissionSettings || row.activity.submissionSettings,
      });
      const maxAttempts = row.maxAttempts == null ? null : Number(row.maxAttempts);
      const used = row.attemptCount || 0;
      setRetryPrompt({
        showPoints: vis.showTotalPoints,
        attemptsLeft: maxAttempts == null ? null : Math.max(0, maxAttempts - used),
      });
    }
    if (row.activity?.type === "exam") {
      setExamCheer(finishedExamLine(row.activity.title));
      return;
    }
    if (offerRetry) return;
    void advanceToNext();
  };

  useEffect(() => {
    if (!activityId) return;
    let cancelled = false;
    saveGate.current.epoch += 1;
    saveGate.current.queued = false;
    autoCompletedFor.current = null;
    setRetryPrompt(null);
    setExamCheer(null);
    setAttemptsOpen(false);
    setError("");
    setPathLocked(false);
    setPayload(null);
    setLocalWatch({});
    post<{ success: boolean; data: OpenPayload }>(
      `/api/student/activities/${encodeURIComponent(activityId)}/open`,
      {},
    )
      .then((env) => {
        if (cancelled) return;
        applyPayload(env.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setPathLocked(err instanceof ApiError && err.code === "PATH_LOCKED");
        setError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  const activity = payload?.activity || null;
  const progress = payload?.progress || null;
  const questions = payload?.questions || [];
  const submission = payload?.submission || null;
  const timing = payload?.timing || null;
  const dwellMinutes = activity?.continueSettings?.minDwellMinutes || 0;
  // The clock is running while a timed attempt is open (Start pressed, not
  // yet handed in). The deadline is the server's; this only counts towards it.
  const clockRunning = Boolean(timing?.started && timing.deadline && submission?.status === "draft");
  const deadlineMs = clockRunning && timing?.deadline ? new Date(timing.deadline).getTime() : 0;
  useEffect(() => {
    const dwelling = dwellMinutes > 0 && progress?.status !== "completed";
    if (!dwelling && !clockRunning) return;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [dwellMinutes, progress?.status, clockRunning]);

  // Time is up: hand in what is there, once. The server will accept these
  // answers inside its grace and otherwise settle the saved draft — either
  // way the student sees a result, not a clock at zero.
  useEffect(() => {
    if (!clockRunning || !submission || deadlineMs > now) return;
    if (autoSubmittedFor.current === submission.id) return;
    autoSubmittedFor.current = submission.id;
    saveGate.current.epoch += 1;
    saveGate.current.queued = false;
    setTimeRanOut(true);
    setSubmitting(true);
    postWork("submit", { answers: answersRef.current })
      .then((next) => afterHandIn(next))
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setSubmitting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, clockRunning, deadlineMs, submission?.id]);

  const results = useMemo(() => resultsFromSubmission(submission), [submission]);

  /**
   * Answers save on their own, so the student never has to press Save.
   *
   * Debounced: a child changing their mind three times should cost one write,
   * not three. Skipped once the attempt is locked, and skipped on the first
   * render so simply opening an activity does not create an empty draft.
   */
  useEffect(() => {
    // Derived here rather than from the consts below: hooks must run before the
    // early returns, and those consts are computed after them.
    if (submission?.status !== "draft") return;
    if (JSON.stringify(answers) === loadedAnswers.current) return;
    const t = window.setTimeout(() => void saveAnswers(), 900);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, submission?.status]);

  useEffect(() => {
    if (saveNotice !== "saved") return;
    const t = window.setTimeout(() => setSaveNotice(null), 1700);
    return () => window.clearTimeout(t);
  }, [saveNotice]);

  // A handed-in paper does not wait on Finish. Once submit (and any dwell /
  // discussion gate) already unlocks complete, mark the step done so the path
  // can move — the locked form then offers Try Again, not a second submit.
  useEffect(() => {
    const row = payload;
    if (!row?.activity || !row.progress) return;
    if (row.progress.status === "completed") return;
    if (row.submission?.status !== "submitted") return;
    if (!row.unlocksComplete) return;
    const settings = row.activity.continueSettings;
    const discussionGate = Boolean(
      settings?.discussion?.replyToPrompt || (settings?.discussion?.replyToOthers || 0) > 0,
    );
    if (discussionGate && !row.discussion?.satisfied) return;
    const dwell = settings?.minDwellMinutes || 0;
    if (dwellRemaining(row.progress.openedAt, dwell, now) > 0) return;
    if (
      settings?.requireWatch &&
      !watchMapSatisfied(partsFromBody(row.activity.body), watchRef.current)
    ) {
      return;
    }
    if (autoCompletedFor.current === row.activity.id) return;
    autoCompletedFor.current = row.activity.id;
    setCompleting(true);
    postWork("complete", { watch: watchRef.current })
      .catch((err) => {
        autoCompletedFor.current = null;
        setError(errorMessage(err));
      })
      .finally(() => setCompleting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, now]);

  useEffect(() => {
    if (!tryAgainCue) return;
    const timer = window.setTimeout(() => setTryAgainCue(0), 3000);
    return () => window.clearTimeout(timer);
  }, [tryAgainCue]);

  useEffect(() => {
    if (!attemptsOpen) return;
    const onPointer = (event: MouseEvent) => {
      const root = attemptsMenuRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setAttemptsOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAttemptsOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [attemptsOpen]);

  useEffect(() => {
    if (!activityId) return;
    let cancelled = false;
    void api<{ success: boolean; data: WorkThreadData }>(
      `/api/student/activities/${encodeURIComponent(activityId)}/thread`,
    )
      .then((envelope) => {
        if (!cancelled) setWorkThread(envelope.data);
      })
      .catch(() => {
        if (!cancelled) setWorkThread(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activityId, payload?.progress?.id, payload?.attemptCount, payload?.submission?.id, payload?.submission?.status]);

  useEffect(() => {
    if (payload?.submission?.status === "submitted" && payload.submission.id) {
      setThreadTab(payload.submission.id);
    }
  }, [payload?.submission?.id, payload?.submission?.status]);

  if (error && !payload) {
    return (
      <div className="student-page">
        <button type="button" className="student-text-link" onClick={() => navigate(-1)}>
          <ChevronLeft size={15} /> Back
        </button>
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
        {pathLocked && (
          <p className="student-muted" style={{ marginTop: 12 }}>
            Complete the earlier activity on the learning path, then come back.
          </p>
        )}
        <Link className="student-text-link" to="/student" style={{ marginTop: 16 }}>
          Back to Today
        </Link>
      </div>
    );
  }

  if (!payload || !activity || !progress) {
    return <ActivityOpeningSkeleton />;
  }

  const Icon = ITEM_ICON[activity.type] || ClipboardCheck;
  const typeLabel = ITEM_LABEL[activity.type] || activity.type;
  const work = payload;
  const visibility = resolveSubmissionSettings({
    submissionSettings: payload.submissionSettings || activity.submissionSettings,
  });
  const showChat = chatEnabled;
  const done = progress.status === "completed";
  const parts = partsFromBody(activity.body);
  const terms = keyTermsFromContent(activity.content);
  const decks = normalizeSlideDecks(activity.content?.decks);
  const slideMode = inferSlideMode(activity.content?.slideMode, decks);
  const hasContent = parts.length > 0 || terms.length > 0 || decks.some((deck) => deck.url || deck.title);
  const neighbors = payload.neighbors || { previous: null, next: null };
  const goNeighbor = (step: Neighbor | null | undefined) => {
    if (!step || step.locked) return;
    navigate(`/student/activity/${step.id}`);
  };
  const hasQuestions = Boolean(activity.hasQuestions);
  const knowledgeRequired = Boolean(activity.continueSettings?.submitQuestions);
  const discussionGate = Boolean(
    activity.continueSettings?.discussion.replyToPrompt ||
      (activity.continueSettings?.discussion.replyToOthers || 0) > 0,
  );
  const remaining = dwellRemaining(progress.openedAt, dwellMinutes, now);
  const requireWatch = Boolean(activity.continueSettings?.requireWatch);
  const watchGate = requireWatch && lockableVideoParts(parts).length > 0 && !done;
  const watchReady = !watchGate || watchMapSatisfied(parts, watchMap);
  const waitingOnWatch = watchGate && !watchReady;
  const draft = submission?.status === "draft";
  const submitted = submission?.status === "submitted";
  const questionsLocked = Boolean(submitted && !draft);
  const attemptRows = work.attempts || [];
  const newAttempt = Boolean(draft && (work.attemptCount || 0) > 0);
  const hasOpenDraft = attemptRows.some((row) => row.status === "draft");
  const answersReady = responsesAreComplete(questions, answers);
  const discussionReady = !discussionGate || Boolean(payload.discussion?.satisfied);
  const dueLocked = Boolean(work.dueLocked);
  const workClosed = dueLocked && !submitted && !Boolean(payload.discussion?.satisfied);
  const canComplete =
    !done &&
    Boolean(work.unlocksComplete || !knowledgeRequired) &&
    discussionReady &&
    remaining <= 0 &&
    watchReady &&
    (!dueLocked || submitted || Boolean(payload.discussion?.satisfied));
  const busy = completing || saving || submitting || switchingAttempt;
  const attemptLabel =
    work.maxAttempts == null
      ? `Attempt ${submission?.attempt || (work.attemptCount || 0) + 1}`
      : `Attempt ${submission?.attempt || work.attemptCount || 0} of ${work.maxAttempts}`;
  const attemptsLeft = work.maxAttempts == null ? null : Math.max(0, work.maxAttempts - (work.attemptCount || 0));
  const timed = Boolean(timing && timing.limitMinutes > 0);
  const isExam = activity.type === "exam";
  // A timed activity, and every exam, shows its instructions and a Start
  // button first. The questions are not on the page — or in the response —
  // until that click. A submitted exam stays closed.
  const examClosed = isExam && submitted && !draft;
  // A reading's version number goes up when the text is saved. That is not a
  // grade, and a first visit has nothing stored to compare. Only a handed-in
  // paper that no longer matches the live activity counts.
  const lessonChanged = Boolean(work.lessonChanged);
  const viewingHistory = Boolean(work.viewingHistory);
  const newVersionHasQuestions =
    typeof work.liveHasQuestions === "boolean" ? work.liveHasQuestions : questions.length > 0;
  // The current lesson is on screen, but the graded paper was an older version.
  // Do not treat that as a blank quiz they still have to answer.
  const staleLanding = lessonChanged && !viewingHistory && !draft;
  const holdForTryAgain = staleLanding && newVersionHasQuestions && Boolean(work.canRetry);
  const nudgeTryAgain = () => {
    setTryAgainCue((n) => n + 1);
    tryAgainRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const needsStart =
    !staleLanding &&
    (isExam
      ? hasQuestions && !draft && !submitted && !done && !workClosed
      : timed && hasQuestions && !done && !submission && attemptsLeft !== 0 && !workClosed);
  const secondsLeft = clockRunning ? Math.max(0, Math.ceil((deadlineMs - now) / 1000)) : 0;
  /** The single next step. The quiet row reads this so it never repeats it. */
  const primaryAction: "start" | "check" | "retry" | "finish" | "back" = workClosed
    ? "back"
    : needsStart
      ? "start"
      : staleLanding && newVersionHasQuestions && work.canRetry
        ? "retry"
        : (staleLanding || (viewingHistory && lessonChanged)) && !newVersionHasQuestions
          ? "finish"
          : staleLanding
            ? "back"
            : knowledgeRequired && questions.length > 0 && !questionsLocked
              ? "check"
              : knowledgeRequired && questionsLocked && work.canRetry
                ? "retry"
                : knowledgeRequired && questionsLocked
                  ? "back"
                  : !done && (canComplete || waitingOnWatch)
                    ? "finish"
                    : "back";
  const reviewCopy =
    hasQuestions && questionsLocked && submission
      ? submittedReviewCopy({
          attempt: submission.attempt,
          attemptCount: work.attemptCount || 0,
          isPrimary: submission.isPrimary,
          submittedAt: submission.submittedAt,
          canRetry: Boolean(work.canRetry),
          hasOpenDraft: hasOpenDraft && !draft,
        })
      : null;
  // Finish / Start / Submit is the work on the right, so Next stays quiet.
  // After a marked paper, Try Again and Next are both ways forward — Next
  // grows into the emphasized control so it is not missed beside Try Again.
  const canGoNext = Boolean(neighbors.next && !neighbors.next.locked);
  const nextIsForward = canGoNext && (primaryAction === "back" || primaryAction === "retry");
  const nextArrives = canGoNext && primaryAction === "retry";
  // A retry draft already has a counted paper. Next is a way out without
  // handing this try in — say so next to the button.
  const retryDraft =
    Boolean(draft && (work.attemptCount || 0) > 0) &&
    Boolean(neighbors.next && !neighbors.next.locked);
  const grade =
    progress?.pointsEarned != null && progress?.pointsPossible != null
      ? { earned: progress.pointsEarned, possible: progress.pointsPossible }
      : null;
  const attemptDiffers =
    grade != null &&
    submission != null &&
    (grade.earned !== submission.pointsEarned || grade.possible !== submission.pointsPossible);
  const scoreNotice =
    submitted && submission && visibility.showTotalPoints
      ? scoreNoticeCopy({
          earned: grade ? grade.earned : submission.pointsEarned,
          possible: grade ? grade.possible : submission.pointsPossible,
          pending: attemptDiffers ? null : submission.pointsPending,
          gradingPending: attemptDiffers ? false : submission.gradingStatus === "pending",
          showIncorrect: visibility.showIncorrect,
          showQuestionPoints: visibility.showQuestionPoints,
          passed: work.passed ?? null,
          attemptEarned: attemptDiffers ? submission.pointsEarned : null,
          attemptPossible: attemptDiffers ? submission.pointsPossible : null,
        })
      : null;

  const markComplete = async () => {
    if (busy) return;
    await advanceToNext();
  };

  const saveAnswers = async () => {
    const row = payloadRef.current;
    if (row?.submission?.status !== "draft") return;
    if (saveGate.current.inFlight) {
      saveGate.current.queued = true;
      return;
    }
    saveGate.current.inFlight = true;
    setSaving(true);
    setSaveNotice("saving");
    setError("");
    const epoch = saveGate.current.epoch;
    let wrote = false;
    try {
      do {
        saveGate.current.queued = false;
        const sent = answersRef.current;
        const snapshot = JSON.stringify(sent);
        if (snapshot === loadedAnswers.current) break;
        await postWork(
          "answers",
          { answers: sent, submissionId: payloadRef.current?.submission?.id },
          "ack",
          epoch,
        );
        wrote = true;
        if (epoch !== saveGate.current.epoch) break;
      } while (
        saveGate.current.queued ||
        JSON.stringify(answersRef.current) !== loadedAnswers.current
      );
      if (epoch === saveGate.current.epoch) setSaveNotice(wrote ? "saved" : null);
    } catch (err) {
      if (epoch === saveGate.current.epoch) {
        setError(errorMessage(err));
        setSaveNotice(null);
      }
    } finally {
      saveGate.current.inFlight = false;
      setSaving(false);
    }
  };

  const submitAnswers = async () => {
    if (submitting || questionsLocked) return;
    if (!responsesAreComplete(questions, answersRef.current)) {
      setError("Answer every question before submitting");
      return;
    }
    saveGate.current.epoch += 1;
    saveGate.current.queued = false;
    setSubmitting(true);
    setError("");
    try {
      const next = await postWork("submit", { answers: answersRef.current });
      afterHandIn(next);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const start = async () => {
    if (busy) return;
    saveGate.current.epoch += 1;
    saveGate.current.queued = false;
    setSaving(true);
    setError("");
    setTimeRanOut(false);
    try {
      await postWork("start");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const retry = async () => {
    if (busy || !work.canRetry) return;
    saveGate.current.epoch += 1;
    saveGate.current.queued = false;
    setSaving(true);
    setError("");
    setTimeRanOut(false);
    try {
      // A timed retry is a fresh clock with a blank paper — carrying the last
      // answers over would hand the student a head start on a timed exam.
      if (timed) await postWork("start");
      else await postWork("answers", { answers: answersFromSubmission(work.primary || submission), carry: true });
      setAttemptsOpen(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openCurrentLesson = async () => {
    if (!activityId || switchingAttempt || completing || submitting) return;
    if (!viewingHistory) {
      setAttemptsOpen(false);
      return;
    }
    saveGate.current.epoch += 1;
    saveGate.current.queued = false;
    const epoch = saveGate.current.epoch;
    setAttemptsOpen(false);
    setSwitchingAttempt(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: OpenPayload }>(
        `/api/student/activities/${encodeURIComponent(activityId)}/open`,
        {},
      );
      if (epoch !== saveGate.current.epoch) return;
      applyPayload(env.data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSwitchingAttempt(false);
    }
  };

  const openAttempt = async (id: string) => {
    if (!activityId || id === submission?.id || switchingAttempt || completing || submitting) return;
    saveGate.current.epoch += 1;
    saveGate.current.queued = false;
    const epoch = saveGate.current.epoch;
    setAttemptsOpen(false);
    setSwitchingAttempt(true);
    setError("");
    try {
      const live = payloadRef.current?.submission;
      if (live?.status === "draft") {
        const snapshot = JSON.stringify(answersRef.current);
        if (snapshot !== loadedAnswers.current) {
          await post(`/api/student/activities/${encodeURIComponent(activityId)}/answers`, {
            answers: answersRef.current,
            submissionId: live.id,
          });
        }
      }
      if (epoch !== saveGate.current.epoch) return;
      const env = await post<{ success: boolean; data: OpenPayload }>(
        `/api/student/activities/${encodeURIComponent(activityId)}/open`,
        { submissionId: id },
      );
      if (epoch !== saveGate.current.epoch) return;
      applyPayload(env.data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSwitchingAttempt(false);
    }
  };

  return (
    <div className={`student-page${switchingAttempt ? " is-switching" : ""}`} aria-busy={switchingAttempt}>
      <SaveToast notice={saveNotice} />
      <Link
        className="student-text-link student-page__back"
        to={`/student/path/${activity.courseId}`}
        state={{ returnToActivityId: activity.id }}
      >
        <ChevronLeft size={15} /> Back to the path
      </Link>
      {switchingAttempt ? (
        <div className="student-switch-overlay" role="status" aria-live="polite">
          <span className="student-vh">Loading attempt</span>
          <div className="student-ghost student-ghost--link" aria-hidden="true" />
          <ActivityChromeSkeleton />
        </div>
      ) : null}

      {error ? (
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      ) : null}

      {newAttempt ? (
        <div className="student-notice student-notice--lock" role="status" style={{ marginTop: 12 }}>
          <strong>This is a new attempt you have started.</strong>
          <p>
            Your earlier tries are still on file. Tap the attempt chip to look at one — this paper is
            a fresh try, not the one you already handed in.
          </p>
        </div>
      ) : reviewCopy && !examClosed ? (
        <div className="student-notice student-notice--lock" role="status" style={{ marginTop: 12 }}>
          <strong>
            <Lock size={14} aria-hidden /> {reviewCopy.title}
          </strong>
          <p>{reviewCopy.body}</p>
        </div>
      ) : null}

      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">
          <Icon size={12} aria-hidden /> {typeLabel}
        </span>
        <h1>{activity.title || "Untitled activity"}</h1>
        <StudentListen
          text={activitySpeakText({
            title: activity.title,
            parts: examClosed ? [] : parts,
            terms: examClosed ? [] : terms,
          })}
        />
        <div className="student-chips">
          <span className={`student-chip${done && !newAttempt ? " student-chip--accent" : ""}`}>
            {newAttempt ? (
              <>
                <CircleDashed size={13} aria-hidden />
                New attempt
              </>
            ) : done ? (
              <>
                <Check size={13} aria-hidden />
                Complete
              </>
            ) : (
              <>
                <CircleDashed size={13} aria-hidden />
                In progress
              </>
            )}
          </span>
          {/* A new attempt must not keep showing the previous paper's total.
              Ungraded stays ungraded: pending work is ?/y, not 0/y or y/y.
              0 is a real score and still shows. */}
          {(() => {
            if (newAttempt) return null;
            const waitingTeacher =
              Boolean(submitted && submission && submission.gradingStatus === "pending") ||
              Boolean(
                !submission &&
                  progress.pointsPossible &&
                  progress.pointsEarned == null &&
                  (done || payload.discussion?.satisfied),
              );
            const showScore = !(submitted && submission) || visibility.showTotalPoints;
            const possible = submitted && submission
              ? submission.pointsPossible ??
                submission.pointsPending ??
                progress.pointsPossible ??
                activity.pointsPossible
              : progress.pointsPossible ?? activity.pointsPossible;
            const earned = submitted && submission ? submission.pointsEarned : progress.pointsEarned;
            const score =
              !showScore || !possible
                ? null
                : waitingTeacher && earned == null
                  ? `?/${possible} points`
                  : earned != null
                    ? `${earned} / ${possible} points`
                    : `${possible} points`;
            if (!score && !waitingTeacher) return null;
            return (
              <span className="student-chips__score">
                {score ? <span className="student-chip">{score}</span> : null}
                {waitingTeacher ? (
                  <span className="student-chip student-chip--wait">Waiting for teacher</span>
                ) : null}
              </span>
            );
          })()}
          {(lessonChanged || (hasQuestions && attemptRows.length > 1)) &&
          attemptRows.some((row) => row.status === "submitted") &&
          !examClosed ? (
            <div className="student-attempts-wrap" ref={attemptsMenuRef}>
              <button
                type="button"
                className="student-chip"
                aria-haspopup="menu"
                aria-expanded={attemptsOpen}
                aria-controls="student-attempts-menu"
                aria-label={
                  lessonChanged && !viewingHistory
                    ? "Attempts. Open the submission you were scored on."
                    : `${attemptLabel}. Your attempts`
                }
                onClick={() => setAttemptsOpen((open) => !open)}
              >
                <History size={13} aria-hidden />
                {lessonChanged && !viewingHistory ? "Attempts" : attemptLabel}
              </button>
              {attemptsOpen ? (
                <div className="student-attempts-pop" id="student-attempts-menu" role="menu">
                  <p className="student-attempts-pop__title">Your attempts</p>
                  <ul className="student-attempts">
                    {lessonChanged ? (
                      <li role="none">
                        <button
                          type="button"
                          role="menuitem"
                          className={`student-attempt${!viewingHistory ? " is-active" : ""}`}
                          aria-current={!viewingHistory ? "true" : undefined}
                          disabled={switchingAttempt || completing || submitting || !viewingHistory}
                          onClick={() => void openCurrentLesson()}
                        >
                          <span className="student-attempt-n">Current activity</span>
                          <span className="student-attempt-meta">The updated activity</span>
                        </button>
                      </li>
                    ) : null}
                    {attemptsNewestFirst(attemptRows).map((row) => {
                      const active = row.id === submission?.id;
                      const when = formatSubmittedAt(row.submittedAt);
                      return (
                        <li key={row.id} role="none">
                          <button
                            type="button"
                            role="menuitem"
                            className={`student-attempt${active ? " is-active" : ""}`}
                            aria-current={active ? "true" : undefined}
                            disabled={switchingAttempt || completing || submitting || active}
                            onClick={() => void openAttempt(row.id)}
                          >
                            <span className="student-attempt-n">
                              Attempt {row.attempt}
                              {row.isPrimary ? (
                                <Star
                                  className="student-attempt-star"
                                  size={13}
                                  strokeWidth={1.5}
                                  fill="currentColor"
                                  aria-label="The attempt that counts"
                                />
                              ) : null}
                            </span>
                            {when ? <span className="student-attempt-meta">{when}</span> : null}
                            <span className="student-attempt-meta">{formatAttemptScore(row)}</span>
                            {row.status === "submitted" && row.gradingStatus === "pending" ? (
                              <span className="student-attempt-meta">Needs a mark</span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : hasQuestions ? (
            <span className="student-chip">{attemptLabel}</span>
          ) : null}
          {clockRunning ? (
            <span
              className={`student-chip${secondsLeft <= 60 ? " student-chip--accent" : ""}`}
              role="timer"
              aria-live="off"
            >
              <Clock size={13} aria-hidden /> {clock(secondsLeft)} left
            </span>
          ) : timed && !submission ? (
            <span className="student-chip">
              <Clock size={13} aria-hidden /> {timing?.limitMinutes} min limit
            </span>
          ) : null}
          {activity.dueAt ? (
            <span className={`student-chip${dueLocked ? " student-chip--accent" : ""}`}>
              {dueLocked
                ? `Closed ${new Date(activity.dueAt).toLocaleDateString()}`
                : `Due ${new Date(activity.dueAt).toLocaleDateString()}`}
            </span>
          ) : null}
        </div>
      </div>

      <StudentKnowledgeBanner
        show={knowledgeRequired && !done && !work.unlocksComplete && !needsStart && !examClosed}
      />

      {!examClosed && parts.length ? (
        <div className="student-reading">
          <StudentBodyParts
            parts={parts}
            activityId={activity.id}
            watchLock={
              watchGate
                ? {
                    activityId: activity.id,
                    enabled: true,
                    watch: watchMap,
                    onWatch: (key, entry) =>
                      setLocalWatch((prev) => ({ ...prev, [key]: mergeWatchEntry(prev[key], entry) })),
                  }
                : undefined
            }
          />
        </div>
      ) : null}

      {!examClosed && terms.length ? <StudentKeyTerms terms={terms} heading={activity.type !== "key_terms"} /> : null}

      {!examClosed && decks.length ? (
        <StudentSlideDecks decks={decks} mode={slideMode} heading={activity.type !== "slides"} />
      ) : null}

      {!hasContent && !hasQuestions && !payload.discussion ? (
        <div className="student-empty">
          <div>
            <span className="student-empty__icon" aria-hidden>
              <Icon size={26} />
            </span>
            <strong>Nothing to read here yet</strong>
            <p>Your teacher has not added content to this activity.</p>
          </div>
        </div>
      ) : null}

      {examClosed ? (
        <div className="student-notice student-notice--lock" role="status" style={{ marginTop: 16 }}>
          <strong>This exam is closed.</strong>
          <p>You already submitted it. The questions are not available anymore.</p>
        </div>
      ) : null}

      {needsStart ? (
        <div className="student-notice" style={{ marginTop: 16 }}>
          {isExam ? (
            <>
              <strong>Read the instructions, then start.</strong>
              <p>
                The questions stay hidden until you press Start Exam. After you submit, this exam
                closes and you cannot open it again.
                {timed
                  ? ` You will have ${timing?.limitMinutes} minute${timing?.limitMinutes === 1 ? "" : "s"} from the moment you press Start Exam, and the clock keeps running if you leave the page.`
                  : ""}
              </p>
            </>
          ) : (
            <>
              <strong>This {typeLabel.toLowerCase()} is timed.</strong> You will have{" "}
              {timing?.limitMinutes} minute{timing?.limitMinutes === 1 ? "" : "s"} from the moment you
              press {startLabel(activity.type)}, and the clock keeps running if you leave the page.
              When it reaches zero your answers are handed in as they are.
              {attemptsLeft != null
                ? ` You have ${attemptsLeft} ${attemptsLeft === 1 ? "attempt" : "attempts"} left.`
                : ""}
            </>
          )}
        </div>
      ) : null}

      {timeRanOut && submitted ? (
        <div className="student-notice" style={{ marginTop: 16 }}>
          Time ran out, so your answers were handed in as they were.
        </div>
      ) : null}

      {hasQuestions && !needsStart && !examClosed && !(lessonChanged && !newVersionHasQuestions) ? (
        <div className="student-section" style={{ marginTop: 16 }}>
          <div className="student-section__head">
            <h2>Questions</h2>
          </div>
          {questions.length === 0 ? (
            <p className="student-muted">No questions here yet.</p>
          ) : (
            <>
            {scoreNotice ? (
              <div className="student-notice student-notice--lock" role="status" style={{ marginBottom: 14 }}>
                <strong>{scoreNotice.title}</strong>
                <p>{scoreNotice.body}</p>
              </div>
            ) : submitted && submission && !visibility.showTotalPoints ? (
              <p className="student-muted">Your answers are in.</p>
            ) : null}

            <div
              className={holdForTryAgain ? "student-questions-hold" : undefined}
              onClick={holdForTryAgain ? nudgeTryAgain : undefined}
            >
            <StudentQuestions
              activityId={activity.id}
              submissionId={submission?.id}
              questions={questions}
              value={answers}
              onChange={(id, next) => setAnswers((prev) => ({ ...prev, [id]: next }))}
              disabled={questionsLocked || holdForTryAgain || (done && !draft)}
              results={results}
              showIncorrect={visibility.showIncorrect}
              showAnswerKey={visibility.showAnswerKey}
              showQuestionPoints={visibility.showQuestionPoints}
            />
            </div>
            </>
          )}
        </div>
      ) : null}

      {payload.discussion ? (
        <div className="student-section" style={{ marginTop: 16 }}>
          <StudentDiscussion
            activityId={activity.id}
            discussion={payload.discussion}
            disabled={dueLocked}
            onChange={(next) => {
              setPayload((prev) => (prev ? { ...prev, discussion: next } : prev));
              if (!activityId) return;
              post<{ success: boolean; data: OpenPayload }>(
                `/api/student/activities/${encodeURIComponent(activityId)}/open`,
                {},
              )
                .then((env) => applyPayload(env.data))
                .catch(() => {});
            }}
          />
        </div>
      ) : null}
      {(submitted && submission?.teacherComment) || progress.teacherComment ? (
        <div className="student-notice" style={{ marginTop: 16 }}>
          <strong>From your teacher</strong>
          <p>{submission?.teacherComment || progress.teacherComment}</p>
        </div>
      ) : null}
      {dueLocked ? (
        <div className="student-notice" style={{ marginTop: 16 }}>
          <strong>This assignment is closed.</strong> The due date has passed
          {submitted || payload.discussion?.satisfied
            ? ", but you can still read what you turned in."
            : ", so it cannot be submitted."}
        </div>
      ) : null}
      {!done && remaining > 0 ? (
        <div className="student-notice" style={{ marginTop: 16 }}>
          Complete is available after {dwellMinutes} minute{dwellMinutes === 1 ? "" : "s"} on this page
          ({Math.ceil(remaining)} left).
        </div>
      ) : null}
      {waitingOnWatch ? (
        <div className="student-notice" style={{ marginTop: 16 }}>
          {lockableVideoParts(parts).length === 1
            ? "Watch the video to the end before Complete is available."
            : "Watch every video to the end before Complete is available."}
        </div>
      ) : null}
      {/* No pass-mark warning. A student who is out of attempts and under the
          threshold moves on with the score they earned — telling them they
          "need 70%" when they can no longer reach it is just a scold. When a
          retry IS available the Try Again link already offers it. */}

      {/* Work (Start / Submit / Finish) is the right-hand action. Previous /
        * Next are quieter, on the left — used less, so they should not
        * compete. Back to the path already lives at the top of the page.
        * The row sits under the activity; it rides that slot until the slot
        * would leave the column, then stays on the floor (long reading /
        * many questions). */}
      <div
        ref={actionsSlotRef}
        className="student-actions-slot"
        style={pinActions ? { height: pinActions.height } : undefined}
      >
        <div
          ref={actionsBarRef}
          className={`student-actions-dock${pinActions ? " is-pinned" : ""}${pinActions?.frozen ? " is-frozen" : ""}`}
          style={
            pinActions
              ? {
                  left: pinActions.left,
                  width: pinActions.width,
                  bottom: pinActions.bottom,
                  transform: `translate3d(0, ${pinActions.translateY}px, 0)`,
                }
              : undefined
          }
        >
          <div className="student-actions">
            <div className="student-step-nav" role="navigation" aria-label="Activity">
              <button
                type="button"
                className="student-text-link"
                disabled={!neighbors.previous || neighbors.previous.locked}
                onClick={() => goNeighbor(neighbors.previous)}
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <button
                type="button"
                className={
                  nextIsForward
                    ? `student-step-nav__next is-forward${nextArrives ? " is-arrive" : ""}`
                    : "student-text-link"
                }
                disabled={!canGoNext}
                onClick={() => goNeighbor(neighbors.next)}
              >
                Next <ChevronRight size={nextIsForward ? 16 : 14} />
              </button>
              {retryDraft ? (
                <span className="student-step-nav__hint">
                  <span className="student-step-nav__hint-icon" aria-hidden>
                    <TriangleAlert size={12} />
                  </span>
                  If you don’t want to finish this attempt, click Next to move on.
                </span>
              ) : null}
            </div>

            <div className="student-actions__work">
              {primaryAction === "start" ? (
                <button
                  type="button"
                  className="student-btn student-btn--primary"
                  disabled={busy}
                  onClick={() => void start()}
                >
                  {saving ? "Starting…" : startLabel(activity.type)}
                </button>
              ) : null}
              {primaryAction === "check" ? (
                <button
                  type="button"
                  className="student-btn student-btn--primary"
                  disabled={submitting || !answersReady}
                  onClick={() => void submitAnswers()}
                >
                  {submitting ? "Submitting…" : answersReady ? "Submit answers" : "Answer every question"}
                </button>
              ) : null}
              {primaryAction === "retry" ? (
                <div className="student-retry" ref={tryAgainRef}>
                  {tryAgainCue ? (
                    <p className="student-try-cue" role="status">
                      Click Try Again to start a new attempt.
                    </p>
                  ) : null}
                  <span id="student-retry-hint" className="student-retry__hint">
                    {lessonChanged
                      ? "This new attempt is for the updated activity."
                      : retryHint(attemptsLeft, canGoNext)}
                  </span>
                  <button
                    type="button"
                    key={tryAgainCue || "try-again"}
                    className={`student-btn student-btn--primary${tryAgainCue ? " is-jump" : ""}`}
                    disabled={busy}
                    aria-describedby="student-retry-hint"
                    onClick={() => (lessonChanged ? setVersionConfirm("retry") : void retry())}
                  >
                    {saving ? "Starting…" : "Try Again"}
                  </button>
                </div>
              ) : null}
              {primaryAction === "finish" && hasQuestions && questions.length > 0 && !questionsLocked ? (
                <button
                  type="button"
                  className="student-text-link"
                  disabled={submitting || !answersReady}
                  onClick={() => void submitAnswers()}
                >
                  {submitting ? "Submitting…" : "Submit answers"}
                </button>
              ) : null}
              {primaryAction === "finish" ? (
                <button
                  type="button"
                  className="student-btn student-btn--primary"
                  disabled={completing || waitingOnWatch}
                  onClick={() =>
                    lessonChanged && !newVersionHasQuestions
                      ? setVersionConfirm("finish")
                      : void markComplete()
                  }
                >
                  {completing
                    ? "Saving…"
                    : waitingOnWatch
                      ? "Watch to the end"
                      : lessonChanged && !newVersionHasQuestions
                        ? "Finish"
                        : activity.type === "discussion"
                        ? "Finish"
                        : neighbors.next && !neighbors.next.locked
                          ? "Finish and continue"
                          : "Finish"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {showChat ? (
        <div
          ref={chatDockRef}
          className={`student-chat-dock${pinChat ? " is-pinned" : ""}`}
          style={
            pinChat
              ? { top: pinChat.top, right: pinChat.right }
              : { visibility: "hidden" }
          }
        >
          <WorkThreadTrigger open={threadOpen} onClick={() => setThreadOpen((on) => !on)} />
        </div>
      ) : null}
      {showChat && threadOpen && pinChat
        ? createPortal(
            <div
              id="al-work-thread-panel"
              className="al-work-thread-panel student-chat-panel"
              role="dialog"
              aria-label="Messages"
              style={{
                top: pinChat.top + pinChat.chipH + 8,
                right: pinChat.right,
                height: "auto",
                maxHeight: pinChat.maxPanel,
              }}
            >
              <div className="al-work-thread-panel-head">
                <button
                  type="button"
                  className="al-grade-dock-close"
                  aria-label="Close messages"
                  onClick={() => setThreadOpen(false)}
                  style={{ display: "inline-flex", marginLeft: "auto" }}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              </div>
              <WorkThread
                data={workThread}
                tab={threadTab}
                audience="public"
                sending={threadSending}
                onTabChange={setThreadTab}
                onJumpQuestion={jumpToStudentQuestion}
                onSend={async (body) => {
                  if (!activityId) return;
                  setThreadSending(true);
                  try {
                    const envelope = await post<{ success: boolean; data: WorkThreadData }>(
                      `/api/student/activities/${encodeURIComponent(activityId)}/thread/messages`,
                      { submissionId: messageSubmissionId(threadTab, workThread?.attempts), body },
                    );
                    setWorkThread(envelope.data);
                  } catch (err) {
                    setError(errorMessage(err));
                  } finally {
                    setThreadSending(false);
                  }
                }}
              />
            </div>,
            document.body,
          )
        : null}
      {examCheer ? (
        <ExamCheer
          line={examCheer}
          retryMessage={retryPrompt ? retryPromptCopy(retryPrompt) : null}
          busy={busy}
          onRetry={
            retryPrompt
              ? () => {
                  setExamCheer(null);
                  setRetryPrompt(null);
                  void retry();
                }
              : null
          }
          onContinue={() => {
            setExamCheer(null);
            setRetryPrompt(null);
            void advanceToNext();
          }}
        />
      ) : retryPrompt ? (
        <StudentRetryPrompt
          message={retryPromptCopy(retryPrompt)}
          busy={busy}
          onRetry={() => {
            setRetryPrompt(null);
            void retry();
          }}
          onContinue={() => {
            setRetryPrompt(null);
            void advanceToNext();
          }}
        />
      ) : versionConfirm ? (
        <div className="student-prompt" role="dialog" aria-modal="true" aria-labelledby="student-version-title">
          <div className="student-prompt__scrim" aria-hidden="true" />
          <div className="student-prompt__panel">
            <h2 id="student-version-title">
              {versionConfirm === "retry" ? "Try again?" : "Finish this version?"}
            </h2>
            <p>
              {previousAttemptLine(work.primary, visibility.showTotalPoints, progress
                ? { earned: progress.pointsEarned, possible: progress.pointsPossible ?? null }
                : null)}{" "}
              {versionConfirm === "retry"
                ? "This new attempt is for the updated activity. The one you were scored on stays under Attempts."
                : "This version has no questions. Finishing completes the updated activity. The attempt you were scored on stays under Attempts."}
            </p>
            <div className="student-prompt__actions">
              <button
                type="button"
                className="student-btn student-btn--ghost"
                disabled={busy}
                onClick={() => setVersionConfirm(null)}
              >
                Not now
              </button>
              <button
                type="button"
                className="student-btn student-btn--primary"
                disabled={busy}
                onClick={() => {
                  const chosen = versionConfirm;
                  setVersionConfirm(null);
                  if (chosen === "retry") void retry();
                  else void markComplete();
                }}
              >
                {versionConfirm === "retry" ? "Try Again" : "Finish"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SaveToast({ notice }: { notice: "saving" | "saved" | null }) {
  if (!notice) return null;
  const ok = notice === "saved";
  return (
    <div
      className={`student-save-toast${ok ? " student-save-toast--ok" : ""}`}
      role="status"
      aria-live="polite"
    >
      {ok ? <Check size={14} aria-hidden /> : <Loader2 size={14} className="student-spin" aria-hidden />}
      {ok ? "Saved" : "Saving"}
    </div>
  );
}

function ExamCheer({
  line,
  retryMessage,
  busy,
  onRetry,
  onContinue,
}: {
  line: string;
  retryMessage: string | null;
  busy: boolean;
  onRetry: (() => void) | null;
  onContinue: () => void;
}) {
  return (
    <div className="student-prompt" role="dialog" aria-modal="true" aria-labelledby="student-exam-cheer-title">
      <div className="student-prompt__scrim" aria-hidden="true" />
      <div className="student-prompt__panel">
        <div className="student-prompt__cheer">
          <Mascot pose="celebrating" size={72} />
          <h2 id="student-exam-cheer-title">{line}</h2>
        </div>
        {retryMessage ? <p>{retryMessage}</p> : null}
        <div className="student-prompt__actions">
          <button
            type="button"
            className={onRetry ? "student-btn student-btn--ghost" : "student-btn student-btn--primary"}
            disabled={busy}
            onClick={onContinue}
          >
            Continue
          </button>
          {onRetry ? (
            <button type="button" className="student-btn student-btn--primary" disabled={busy} onClick={onRetry}>
              Try Again
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StudentRetryPrompt({
  message,
  busy,
  onRetry,
  onContinue,
}: {
  message: string;
  busy: boolean;
  onRetry: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="student-prompt" role="dialog" aria-modal="true" aria-labelledby="student-retry-title">
      <div className="student-prompt__scrim" aria-hidden="true" />
      <div className="student-prompt__panel">
        <h2 id="student-retry-title">Try again?</h2>
        <p>{message}</p>
        <div className="student-prompt__actions">
          <button type="button" className="student-btn student-btn--ghost" disabled={busy} onClick={onContinue}>
            Continue
          </button>
          <button type="button" className="student-btn student-btn--primary" disabled={busy} onClick={onRetry}>
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityChromeSkeleton() {
  return (
    <>
      <div className="student-ghost-head">
        <div className="student-ghost student-ghost--chip" />
        <div className="student-ghost student-ghost--title" />
        <div className="student-ghost-row">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--chip" />
        </div>
      </div>
      <div className="student-ghost-block">
        <div className="student-ghost student-ghost--line" />
        <div className="student-ghost student-ghost--line student-ghost--w90" />
        <div className="student-ghost student-ghost--line student-ghost--w75" />
        <div className="student-ghost student-ghost--line student-ghost--w60" />
      </div>
      <div className="student-ghost-card">
        <div className="student-ghost student-ghost--meta" />
        <div className="student-ghost student-ghost--line student-ghost--w80" />
        <div className="student-ghost student-ghost--choice" />
        <div className="student-ghost student-ghost--choice" />
        <div className="student-ghost student-ghost--choice" />
      </div>
      <div className="student-ghost-card">
        <div className="student-ghost student-ghost--meta" />
        <div className="student-ghost student-ghost--line student-ghost--w70" />
        <div className="student-ghost student-ghost--choice" />
        <div className="student-ghost student-ghost--choice" />
      </div>
    </>
  );
}

function ActivityOpeningSkeleton() {
  return (
    <div className="student-page" aria-busy="true" aria-live="polite">
      <span className="student-vh">Opening activity</span>
      <div className="student-ghost student-ghost--link" />
      <ActivityChromeSkeleton />
    </div>
  );
}

export default ActivityView;
